import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  Briefcase, 
  MessageSquare, 
  LogOut, 
  Loader2,
  MapPin,
  Calendar,
  DollarSign,
  Users
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, jobsRes, unreadRes] = await Promise.all([
        axios.get(`${API}/stats/dashboard`),
        axios.get(`${API}/jobs/my`),
        axios.get(`${API}/messages/unread-count`)
      ]);
      setStats(statsRes.data);
      setJobs(jobsRes.data);
      setUnreadCount(unreadRes.data.count);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-900/50 text-green-400 border-green-800',
      in_progress: 'bg-yellow-900/50 text-yellow-400 border-yellow-800',
      completed: 'bg-blue-900/50 text-blue-400 border-blue-800',
      cancelled: 'bg-red-900/50 text-red-400 border-red-800'
    };
    return styles[status] || styles.open;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950" data-testid="customer-dashboard">
      {/* Header */}
      <header className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_clearing-crew/artifacts/qizuc7gv_file_00000000d05471f59cc81c7b9868657c.png" 
                alt="IronLink" 
                className="h-14"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/messages" className="relative">
                <Button variant="ghost" size="icon" data-testid="messages-btn" className="text-stone-400 hover:text-white">
                  <MessageSquare size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </Link>
              <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn" className="text-stone-400 hover:text-white">
                <LogOut size={20} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl text-white uppercase">Welcome, {user?.name}</h1>
            <p className="text-stone-400 mt-1">Manage your land clearing projects</p>
          </div>
          <Link to="/jobs/new">
            <Button 
              data-testid="post-job-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wide rounded-sm"
            >
              <Plus className="mr-2" size={20} />
              Post New Job
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Jobs', value: stats?.total_jobs || 0, icon: Briefcase },
            { label: 'Active', value: stats?.active_jobs || 0, icon: Users },
            { label: 'In Progress', value: stats?.in_progress || 0, icon: Calendar },
            { label: 'Completed', value: stats?.completed || 0, icon: DollarSign }
          ].map((stat, index) => (
            <div key={index} className="industrial-card p-6">
              <div className="flex items-center justify-between">
                <stat.icon className="w-8 h-8 text-orange-500" />
                <span className="font-heading text-4xl text-orange-500">{stat.value}</span>
              </div>
              <p className="text-stone-400 mt-2 uppercase tracking-wide text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Jobs List */}
        <div>
          <h2 className="font-heading text-2xl text-white uppercase mb-4">Your Jobs</h2>
          
          {jobs.length === 0 ? (
            <div className="industrial-card p-12 text-center">
              <Briefcase className="w-16 h-16 text-stone-600 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-white uppercase">No Jobs Yet</h3>
              <p className="text-stone-400 mt-2">Post your first job to get started</p>
              <Link to="/jobs/new">
                <Button className="mt-6 bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase rounded-sm">
                  Post a Job
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <Link 
                  key={job.id} 
                  to={`/jobs/${job.id}`}
                  className="block industrial-card p-6 hover:border-orange-500/50"
                  data-testid={`job-card-${job.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-heading text-xl text-white uppercase">{job.title}</h3>
                        <Badge className={`${getStatusBadge(job.status)} rounded-sm uppercase text-xs`}>
                          {job.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-stone-400 text-sm">
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {job.location}
                        </span>
                        <span>{job.acreage} acres</span>
                        <span className="text-orange-500">{job.bid_count} bids</span>
                      </div>
                    </div>
                    {job.budget_max && (
                      <div className="text-right">
                        <span className="font-heading text-2xl text-orange-500">
                          ${job.budget_min?.toLocaleString()} - ${job.budget_max?.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
